import React from "react";
import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "@/pages/home/components/Header";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";

/* ──────────────────────── data ──────────────────────── */

interface FAQItem {
  question: string;
  answer: React.ReactNode;
}

interface FAQCategory {
  id: string;
  title: string;
  icon: string;
  faqs: FAQItem[];
}

const faqCategories: FAQCategory[] = [
  {
    id: "the-fund-and-how-it-works",
    title: "The Fund and How It Works",
    icon: "ri-building-4-line",
    faqs: [
      {
        question: "What type of Plan is the Musicians' Pension Fund of Canada (MPF Canada)?",
        answer: (
          <p>The Plan is a multi-employer defined benefit pension plan, commonly known as a "target benefit plan." This is a plan in which multiple employers contribute to the Fund to pay specified monthly benefits to Plan members when they retire. Benefits are predetermined by a formula that is based on employer contributions. Accrual rates and/or accrued benefits may be adjusted depending on the Plan's funding status.</p>
        ),
      },
      {
        question: "How can I join the Plan?",
        answer: (
          <p>You're eligible to become a Plan member if you work for an employer who has entered into a collective bargaining agreement with the Canadian Federation of Musicians (CFM), or with one of its affiliated locals. You're also eligible if you work for an employer who has entered into a Participation Agreement with the Trustees and who has agreed to contribute to the Fund on your behalf.</p>
        ),
      },
      {
        question: "What kind of work must I do to join the Plan?",
        answer: (
          <div>
            <p className="mb-3">You can work as any of the following:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>A musician, including a composer, arranger, copyist, proofreader, librarian, instrumentalist, leader, or contractor; or</li>
              <li>An elected or appointed officer or representative of the CFM or any of its affiliated locals; or</li>
              <li>An officer or clerical employee of the Fund, Musicians' Rights Organization Canada (MROC), or the CFM or any of its affiliated locals.</li>
            </ul>
          </div>
        ),
      },
      {
        question: 'What does "CFM" mean?',
        answer: (
          <p>"CFM" means the Canadian Federation of Musicians and/or The American Federation of Musicians of the United States and Canada, including any of their affiliated Canadian locals.</p>
        ),
      },
      {
        question: "How can I become a Plan member?",
        answer: (
          <div>
            <p className="mb-3">You can become a Plan member in one of three ways:</p>
            <ol className="list-decimal pl-5 space-y-3">
              <li>You have 24 months of vesting service without a six calendar month period with no contributions. This is the way most musicians become members; or</li>
              <li>If you have your first contribution made on your behalf after December 31, 2000, you will become a Plan member on the first day of the calendar year following the calendar year in which you have 700 hours of <a href="https://www.mpfcanada.ca/hrf_faq/what-do-covered-employment-covered-earnings-and-years-maximum-pensionable-earnings-mean/" target="_blank" rel="nofollow" class="text-[#1a8fc1] underline hover:text-[#0d8ab5] transition-colors">covered employment</a> or <a href="https://www.mpfcanada.ca/hrf_faq/what-do-covered-employment-covered-earnings-and-years-maximum-pensionable-earnings-mean/" target="_blank" rel="nofollow" class="text-[#1a8fc1] underline hover:text-[#0d8ab5] transition-colors">covered earnings</a> equal to or greater than 35% of the <a href="https://www.mpfcanada.ca/hrf_faq/what-do-covered-employment-covered-earnings-and-years-maximum-pensionable-earnings-mean/" target="_blank" rel="nofollow" class="text-[#1a8fc1] underline hover:text-[#0d8ab5] transition-colors">Year's Maximum Pensionable Earnings</a>; or</li>
              <li>If the first contribution made on your behalf was before January 1, 2001, the date when you become a Plan member depends on several things. These include the date the first contributions to the Plan were made for you and whether your work was done as a musician or not. Please contact the Fund Office for more information.</li>
            </ol>
          </div>
        ),
      },
      {
        question: 'What do "covered employment", "covered earnings" and "Year\'s Maximum Pensionable Earnings" mean?',
        answer: (
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Covered employment</strong> is the work done for an employer that is required to contribute to the Plan.</li>
            <li><strong>Covered earnings</strong> are scale wages on which pension contributions have been made to the Fund on your behalf.</li>
            <li><strong>Year's Maximum Pensionable Earnings</strong> is a term defined in the Canada Pension Plan for persons subject to the pension law of Quebec or the Quebec Pension Plan. The 2023 amount is $66,600. This amount may change annually.</li>
          </ul>
        ),
      },
      {
        question: "When am I entitled to a pension benefit?",
        answer: (
          <div>
            <p className="mb-3">You're entitled to a pension benefit once you become vested in your benefit. "Vesting" means you have a right to your benefit. You may become vested under any one of the following rules:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>You were a participant who becomes a Plan Member after December 31, 2000; or</li>
              <li>You were already a Member of the Plan on December 31, 2000 and have contributions made on your behalf after December 31, 2000, but before your membership in the Plan terminates; or</li>
              <li>If you became a Plan Member before January 1, 2001 and do not have employer contributions made on your behalf after December 31, 2000, in which case you become vested on the earlier of the date when you have earned at least 24 months of vesting service or you reach age 65 while you are a Plan Member.</li>
            </ul>
          </div>
        ),
      },
      {
        question: "What are some key Plan Definitions?",
        answer: (
          <div>
            <p className="mb-3">Click below to help you understand Key Plan Definitions.</p>
            <a
              href="/wp-content/uploads/2020/10/MPFC_Definitions_FINAL.pdf"
              target="_blank"
              rel="nofollow"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-4 py-2.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
            >
              View Key Plan Definitions
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>
        ),
      },
    ],
  },
  {
    id: "pension-benefits",
    title: "Pension Benefits",
    icon: "ri-hand-coin-line",
    faqs: [
      {
        question: `What type of benefits does the Plan pay?`,
        answer: (
          <div>
            <p className="mb-3">You can receive one of the following types of benefits from the Plan:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Normal pension, starting age 65 or older</li>
              <li>Early pension, starting any time from age 55 but before you reach age 65</li>
              <li>Special Retirement pension</li>
              <li>Disability pension</li>
            </ul>
          </div>
        ),
      },
      {
        question: `Is my pension indexed after retirement?`,
        answer: (
          <div>
            <p className="mb-3">An "indexed pension" is one that is increased periodically to reflect increases in the consumer price index (CPI). The consumer price index (CPI) is a measure of the degree of change in the price of goods and services purchased by Canadian consumers and is determined monthly by Statistics Canada.</p>
            <p className="mb-3">Only about 32% of all defined benefit plans in Canada provide any form of guaranteed indexing after retirement. Most of them are public sector plans where funding comes, at least in part, from taxpayers.</p>
            <p>The Board of Trustees regularly reviews the different types of benefits that the Plan provides, and makes changes periodically based on what it believes is in the best interests of the members. Since very few, if any, multi-employer pension plans like ours provide guaranteed indexing, and since the cost is significant, the Trustees have not implemented guaranteed indexing as part of the menu of benefit provisions under the Plan.</p>
          </div>
        ),
      },
      {
        question: `What is a Special Retirement pension?`,
        answer: (
          <div>
            <p className="mb-3">You may retire on a Special Retirement pension if you:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Are at least 55 and not yet age 65</li>
              <li>Are vested</li>
              <li>Are an active member at age 55 or older</li>
              <li>Have at least 15 vested years for Part 1 contributions and at least 20 vested years for Part 2 contributions in which contributions were made each year on your behalf</li>
              <li>The 15 or 20 years do not include any periods that have been cancelled before you were vested</li>
            </ul>
            <p className="mt-3">Special Retirement pension is only applicable to work done prior to January 1, 2013. You also have to be active as of the pension effective date, meaning you have contributions for an engagement in the 24 month period before the pension effective date.</p>
          </div>
        ),
      },
      {
        question: `What is a Disability Pension?`,
        answer: (
          <div>
            <p className="mb-3">You may be eligible for a Disability pension if you:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Become totally and permanently disabled while you are an active Member of the Plan</li>
              <li>Are not eligible to retire on a Normal pension</li>
              <li>Are vested at the time of your disability</li>
              <li>Are not eligible to receive disability benefits from your employer or under an insurance program provided by your employer</li>
            </ul>
          </div>
        ),
      },
      {
        question: `How much will my benefit be?`,
        answer: (
          <div>
            <p className="mb-3">The amount of your monthly pension benefit will depend on:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Contributions made by your employer or employers for your covered employment</li>
              <li>Whether you receive a Normal, Early, Special Retirement, or Disability pension</li>
              <li>The pension payment form you choose, which is based in part on whether you have a spouse on the date your pension payment begins</li>
            </ul>
          </div>
        ),
      },
      {
        question: `How is my benefit calculated?`,
        answer: (
          <div>
            <p className="mb-3">Your pension is calculated in three parts:</p>
            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li><strong>Part 1</strong> is your pension benefit related to contributions for engagements taking place up to and including December 31, 2010.</li>
              <li><strong>Part 2</strong> is your pension benefit related to contributions for engagements taking place from January 1, 2011 up to and including December 31, 2012.</li>
              <li><strong>Part 3</strong> is your pension benefit related to contributions for engagements taking place on and after January 1, 2013.</li>
            </ul>
            <p className="mb-3">For example, here is how a Normal pension benefit is calculated:</p>
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2.5 pr-3 text-[#0d1f4c] font-semibold text-xs">Contribution Type and Timing</th>
                    <th className="text-right py-2.5 pl-3 text-[#0d1f4c] font-semibold text-xs whitespace-nowrap">Monthly Pension for Each $100 of Contributions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr className="bg-[#f4f8fc]/60">
                    <td className="py-2.5 pr-3 font-semibold text-[#0d1f4c] text-xs">PART 1</td>
                    <td className="py-2.5 pl-3 text-right"></td>
                  </tr>
                  <tr>
                    <td className="py-2.5 pr-3">Contributions for covered employment before 1992</td>
                    <td className="py-2.5 pl-3 text-right font-medium">$3.80</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 pr-3">Contributions for covered employment from January 1, 1992 up to and including April 30, 2006</td>
                    <td className="py-2.5 pl-3 text-right font-medium">$3.70</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 pr-3">Contributions for covered employment from May 1, 2006 up to and including December 31, 2010</td>
                    <td className="py-2.5 pl-3 text-right"></td>
                  </tr>
                  <tr>
                    <td className="py-2.5 pr-3 pl-4 text-gray-500">Contributions up to 10% of scale wages</td>
                    <td className="py-2.5 pl-3 text-right font-medium">$3.70</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 pr-3 pl-4 text-gray-500">Contributions between 10% and 12% of scale wages</td>
                    <td className="py-2.5 pl-3 text-right font-medium">$2.00</td>
                  </tr>
                  <tr className="bg-[#f4f8fc]/60">
                    <td className="py-2.5 pr-3 font-semibold text-[#0d1f4c] text-xs">PART 2</td>
                    <td className="py-2.5 pl-3 text-right"></td>
                  </tr>
                  <tr>
                    <td className="py-2.5 pr-3">Contributions for covered employment from January 1, 2011 up to and including December 31, 2012</td>
                    <td className="py-2.5 pl-3 text-right"></td>
                  </tr>
                  <tr>
                    <td className="py-2.5 pr-3 pl-4 text-gray-500">Contributions up to 10% of scale wages</td>
                    <td className="py-2.5 pl-3 text-right font-medium">$3.25</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 pr-3 pl-4 text-gray-500">Contributions between 10% and 12% of scale wages</td>
                    <td className="py-2.5 pl-3 text-right font-medium">$2.00</td>
                  </tr>
                  <tr className="bg-[#f4f8fc]/60">
                    <td className="py-2.5 pr-3 font-semibold text-[#0d1f4c] text-xs">PART 3</td>
                    <td className="py-2.5 pl-3 text-right"></td>
                  </tr>
                  <tr>
                    <td className="py-2.5 pr-3">All contributions for covered employment on and after January 1, 2013, maximum 18%</td>
                    <td className="py-2.5 pl-3 text-right font-medium">$3.25</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4 mb-2 font-medium">Contributions for this period are divided into two equal amounts to be applied as follows:</p>
            <ul className="list-disc pl-5 space-y-1 mb-3">
              <li>50% is allocated as regular contributions to be used for calculating your benefits.</li>
              <li>50% is allocated as sustaining contributions that will help support the actual cost of your benefits and will not be used for calculating benefits.</li>
            </ul>
            <p className="mb-3">Effectively this is an accrual rate of 1.625 on all contributions made on your behalf.</p>
            <p className="mb-4">For an example of a Normal pension calculation, and for information about the calculation of other types of Plan pensions, please see the Plan Summary.</p>
            <a
              href="/wp-content/uploads/2019/01/MPFCanada_PensionPlan2019-FINAL-s.pdf"
              target="_blank"
              rel="nofollow"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-4 py-2.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
            >
              View Plan Summary
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>
        ),
      },
      {
        question: `Who can be my beneficiary?`,
        answer: (
          <p>Your beneficiary is any person or persons you designate to receive Plan benefits if you die. If you have a spouse on the date your retirement starts, he or she is automatically your beneficiary unless you and your spouse waive your spouse's rights to a benefit. If a member is subject to the pension laws of Quebec and designates his or her spouse as a beneficiary, that designation is irrevocable unless otherwise stipulated in the designation.</p>
        ),
      },
      {
        question: `How can I change my beneficiary?`,
        answer: (
          <div>
            <p className="mb-3">You may change your beneficiary any time by notifying the Fund Office in writing. For the change to be effective, the Fund Office must receive it before any payments are made to your previously designated beneficiary.</p>
            <p>If you have a spouse when you retire, he or she has the right to receive a survivor benefit if you die and is designated automatically as your beneficiary, unless you and your spouse waive that right in writing.</p>
          </div>
        ),
      },
      {
        question: `Is my pension guaranteed?`,
        answer: (
          <div>
            <p className="mb-3">No, your pension is not guaranteed. The benefits provided by this Plan are not guaranteed by the Ontario Government Pension Benefits Guarantee Fund. The Ontario Pension Benefits Guarantee Fund does not apply to multi-employer pension plans.</p>
            <p>In the unlikely event that the Plan is terminated and it is not fully funded, your pension benefit may be subject to a reduction. As this is a Target Benefit Plan, pension benefits may be reduced.</p>
          </div>
        ),
      },
      {
        question: `Will my pension payments increase based on inflation?`,
        answer: (
          <div>
            <p className="mb-3">No. Your pension payments will not increase based on inflation. This increase is often referred to as "indexing."</p>
            <p>Since very few, if any, multi-employer pension plans like ours provide guaranteed indexing, and since the cost of indexing is significant, the Trustees have not implemented guaranteed indexing under the Plan.</p>
          </div>
        ),
      },
      {
        question: `Can I retire before age 65?`,
        answer: (
          <div>
            <p className="mb-3">You can retire as early as age 55 and receive a pension from the Plan if you are vested in your benefit when you retire.</p>
            <p className="mb-3">Your pension will be calculated the same way as a Normal pension. However, because you will receive your pension for a longer period of time than if you retire at age 65, your monthly pension payment will be reduced. The reduction is based on your age at retirement. The younger you are when you retire, the greater your monthly pension payment reduction will be.</p>
            <p className="mb-4">Please see the Plan Summary for more information about early retirement.</p>
            <a
              href="/wp-content/uploads/2019/01/MPFCanada_PensionPlan2019-FINAL-s.pdf"
              target="_blank"
              rel="nofollow"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-4 py-2.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
            >
              View Plan Summary
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>
        ),
      },
      {
        question: `What happens to my benefit if I retire after age 65?`,
        answer: (
          <p>If you retire after age 65, your benefit will continue to accrue until you start your pension. You must commence your pension by December 1 of the year you turn age 71.</p>
        ),
      },
      {
        question: `Can I continue earning benefits after age 71?`,
        answer: (
          <p>No. As per the Income Tax Act, no one can accrue benefits after December 1 of the year they become age 71.</p>
        ),
      },
      {
        question: `What happens to my benefit if I become disabled?`,
        answer: (
          <p>If you are vested in your benefit and you become totally and permanently disabled, and you are not eligible to receive disability benefits from your employer or from an insurance program provided by your employer, you may be entitled to a Disability pension from the Pension Fund.</p>
        ),
      },
      {
        question: `How do the Trustees know that the Plan has enough money to pay benefits to current and future retirees and beneficiaries?`,
        answer: (
          <div>
            <p className="mb-3">Every three years, in compliance with pension law, the Fund uses the services of professional actuaries to evaluate the Plan to confirm that it has enough money to pay benefits to current and future retirees and beneficiaries. This is called an actuarial valuation.</p>
            <p>An actuarial valuation may be conducted more frequently than every three years, at the discretion of the Trustees.</p>
          </div>
        ),
      },
      {
        question: `What is the Temporary/Bridging Pension available to Quebec members taking early retirement?`,
        answer: (
          <div>
            <p className="mb-3">A Quebec member who elects an Early pension has the option to take a Temporary Pension. A Temporary Pension will provide the member with an additional income until age 65, or before if desired.</p>
            <p className="mb-3">The yearly amount of a Temporary Pension is chosen by the member before any pension payments are made. The amount chosen cannot exceed 40% of the Maximum Pensionable Earnings under the Quebec Pension Plan for the year in which payment of a pension begins.</p>
            <p>Taking a Temporary Pension results in larger pension payments during the temporary period than would have otherwise been paid. Therefore, the lifetime pension paid from retirement onwards is actuarially adjusted to take into account the payment of the Temporary Pension.</p>
          </div>
        ),
      },
    ],
  },
  {
    id: "death-benefits",
    title: "Death Benefits",
    icon: "ri-shield-user-line",
    faqs: [
      {
        question: `What happens to my pension benefit if I die before I retire?`,
        answer: (
          <div>
            <p className="mb-3">If you are not vested in your pension benefit and you die before you retire, no benefit is payable to your beneficiary.</p>
            <p className="mb-3">If you are vested in your pension benefit and you die before you retire, your beneficiary will receive a pre-retirement death benefit from the Plan.</p>
            <p className="mb-4">Please see the Plan Summary for more information about a pre-retirement death benefit.</p>
            <a
              href="/wp-content/uploads/2019/01/MPFCanada_PensionPlan2019-FINAL-s.pdf"
              target="_blank"
              rel="nofollow"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-4 py-2.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
            >
              View Plan Summary
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>
        ),
      },
      {
        question: `What happens to my pension benefit if I die after I start receiving my benefit?`,
        answer: (
          <div>
            <p className="mb-3">If you die after you start receiving your pension benefit, your beneficiary may receive a pension benefit, depending on the type of benefit you selected at retirement.</p>
            <p className="mb-4">Please see the Plan Summary for more information, or contact the Fund Office by phone at 1-888-462-6666.</p>
            <div className="flex flex-wrap gap-3">
              <a
                href="/wp-content/uploads/2019/01/MPFCanada_PensionPlan2019-FINAL-s.pdf"
                target="_blank"
                rel="nofollow"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-4 py-2.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
              >
                View Plan Summary
                <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
              <a
                href="mailto:info@mpfcanada.ca"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-4 py-2.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
              >
                Email Fund Office
                <i className="ri-mail-line text-sm"></i>
              </a>
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: "contribution-information",
    title: "Contribution Information",
    icon: "ri-bank-line",
    faqs: [
      {
        question: `How are pension contributions made to the Fund?`,
        answer: (
          <p>Pension contributions are made on your behalf by your employers. The contributions made on your behalf are based on your dates of employment and your scale wages or, if you are a local officer or employee, your earnings. Contributions made to the Fund are held in a trust fund to provide benefits to eligible members and their beneficiaries, and to pay Fund administrative expenses.</p>
        ),
      },
      {
        question: `Can I make my own pension contributions?`,
        answer: (
          <p>No. Employee contributions are not required or permitted. For the Plan to be considered a registered pension plan, the Income Tax Act requires that there be a bona fide employer-employee relationship for every member who participates in the Plan.</p>
        ),
      },
      {
        question: `Can pension contributions be made on negotiated fees?`,
        answer: (
          <p>No. Pension contributions cannot be made on negotiated fees. Contributions can only be made pursuant to a collective bargaining agreement, or pursuant to local engagement contracts, which are based on CFM/AFM scale wages.</p>
        ),
      },
      {
        question: `Can I have pension contributions made on my behalf in excess of 18% scale?`,
        answer: (
          <p>No. The 18% rate cap complies with the Income Tax Act rule that limits total contributions to a registered pension plan to 18% of compensation. Keep in mind that, although contributions up to 18% of scale are permitted, your employer's contribution rate is limited to the rate specified in the collective bargaining agreement or local tariff of fees.</p>
        ),
      },
      {
        question: `If I work outside Canada, do I continue getting pension contributions?`,
        answer: (
          <div>
            <p className="mb-3">If you are temporarily working outside of Canada for a Canadian employer, your employer can continue making pension contributions to the Plan. Note that the Income Tax Act limits the length of time that you can receive pension contributions to the Plan while working in the US.</p>
            <p className="mb-3">If you are working in the US for a US employer, you may, subject to the terms of any applicable collective bargaining agreement, have pension contributions remitted to the Canadian Fund.</p>
            <p className="mb-4">If you have worked in Canada and the US, and pension contributions have been remitted on your behalf to the Canadian Plan and to the US Plan, both plans have provisions that allow the credit in one plan to be recognized as a credit in the other for eligibility and vesting purposes.</p>
            <p className="mb-4">Please see the Plan Summary for more information or contact the Fund Office by email.</p>
            <div className="flex flex-wrap gap-3">
              <a
                href="/wp-content/uploads/2019/01/MPFCanada_PensionPlan2019-FINAL-s.pdf"
                target="_blank"
                rel="nofollow"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-4 py-2.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
              >
                View Plan Summary
                <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
              <a
                href="mailto:info@mpfcanada.ca"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-4 py-2.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
              >
                Email Fund Office
                <i className="ri-mail-line text-sm"></i>
              </a>
            </div>
          </div>
        ),
      },
      {
        question: `If I work for a symphony and am on leave of absence, are pension contributions still made for me?`,
        answer: (
          <div>
            <p className="mb-3">Contributions to the Plan are made based on Collective Bargaining Agreements, which are agreements between the CFM/AFM and employers. If the agreement under which you are covered allows contributions to continue while you are on an approved leave of absence, then contributions will continue.</p>
            <p>Under the Income Tax Act, there are limits on how long contributions can be made during an approved leave of absence.</p>
          </div>
        ),
      },
      {
        question: `Why has my employer reported my pension contributions on my T4/T4A? How does this affect me?`,
        answer: (
          <div>
            <p className="mb-3">The Income Tax Act requires pension adjustments, also known as PAs, to be reported for all pension contributions made to the Plan.</p>
            <p className="mb-3">Each year the amount of the employer contributions made on your behalf to the Fund should be reported on your T4/T4A in the box titled "Pension Adjustment." This amount is used to reduce your RRSP contribution room in the following year.</p>
            <p>The Fund is not your employer and therefore does not report Pension Adjustments. Under the Income Tax Act, it is the employer's responsibility to report the PA.</p>
          </div>
        ),
      },
      {
        question: `I am receiving a pension but I'm still working. Are pension contributions still being made for me?`,
        answer: (
          <div>
            <p className="mb-3">Yes. If you continue working under covered employment after you start receiving pension payments, pension contributions for you will continue being made for you.</p>
            <p className="mb-3">Up until the end of the calendar year in which you turn 71, you will continue to receive your pension from the Plan and may receive an additional pension benefit based on the employer contributions made to the post retirement account.</p>
            <p className="mb-4">Please see the Plan Summary for more information on the post retirement account.</p>
            <p className="mb-4">If you continue working in covered employment after December 31 in the year you become age 71, the Fund is prohibited by the Income Tax Act from accepting contributions made on your behalf. If your employer does make a contribution on your behalf to the Fund, MPF Canada will refund the contribution to your employer.</p>
            <a
              href="/wp-content/uploads/2019/01/MPFCanada_PensionPlan2019-FINAL-s.pdf"
              target="_blank"
              rel="nofollow"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-4 py-2.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
            >
              View Plan Summary
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>
        ),
      },
    ],
  },
  {
    id: "life-events",
    title: "Life Events",
    icon: "ri-calendar-event-line",
    faqs: [
      {
        question: `What should I do when I am preparing for retirement?`,
        answer: (
          <div>
            <p className="mb-3">Here is what you should do when preparing for retirement:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Notify the Fund Office about your retirement at least three months before your intended retirement date. When the Fund Office receives your request, a retirement application and instructions will be mailed or emailed to your home address on file.</li>
              <li>Apply for a pension benefit by completing and submitting the necessary forms to the Fund Office.</li>
              <li>Submit proof of age for yourself and your spouse, and proof of marital status if applicable, when you submit your forms.</li>
            </ul>
            <p className="mt-3">The Fund Office will acknowledge receipt of your application and will tell you if additional information is needed.</p>
          </div>
        ),
      },
      {
        question: `What happens if I get married after I start receiving my pension benefit?`,
        answer: (
          <div>
            <p className="mb-3">If you get married after you start receiving your pension benefit, the Plan will only pay a benefit to your spouse if:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>The payment form you chose at retirement has a guarantee period</li>
              <li>You pass away before the guarantee period is over</li>
              <li>Your spouse is your designated beneficiary</li>
            </ul>
          </div>
        ),
      },
      {
        question: `What happens if I get divorced before or while receiving my pension benefit?`,
        answer: (
          <div>
            <p className="mb-3">If you get divorced before or while receiving a benefit from the Plan, the benefit to which your former spouse may be entitled depends on applicable law and the court documents or agreements related to your divorce.</p>
            <p className="mb-3">Be sure to file any court documents or agreements related to pension benefits and your divorce with the Fund Office as soon as possible.</p>
            <p>If your former spouse is entitled to a portion of your benefit, the benefit you receive will be adjusted accordingly.</p>
          </div>
        ),
      },
      {
        question: `Do I have to pay the Fund to provide marriage breakdown calculations if I become divorced or my marriage is annulled?`,
        answer: (
          <div>
            <p className="mb-3">Yes. The Fund must pay an actuary for marriage breakdown calculations and it would not be equitable for other Plan members to bear the full cost of these calculations.</p>
            <p>The fees charged to members are set by each province and are far less than the actual fees incurred by the Fund.</p>
          </div>
        ),
      },
    ],
  },
  {
    id: "general",
    title: "General",
    icon: "ri-question-answer-line",
    faqs: [
      {
        question: `How can I check my account information?`,
        answer: (
          <div>
            <p className="mb-3">To see the contributions made by employers to your account, visit the MPF Canada Member Portal.</p>
            <p className="mb-3">Enter your Username and Password. Your Username is your email on file with MPF Canada.</p>
            <p className="mb-3">Then, click on the contributions tab to see a list of the pension contributions processed to date on your behalf.</p>
            <p className="mb-3">If you are logging in for the first time or forgot your password, use the "Forgot Password" feature to get a temporary password emailed to you.</p>
            <p className="mb-4">If you need assistance, please call the Fund Office at 1-888-462-6666.</p>
            <a
              href="https://www.mpfcanada.ca/mpfapp/faces/login"
              target="_blank"
              rel="nofollow"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-4 py-2.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
            >
              Access Member Portal
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>
        ),
      },
      {
        question: `How are contributions to the Fund invested?`,
        answer: (
          <div>
            <p className="mb-3">Contributions to the Fund are invested in a diversified portfolio of assets and are managed by professional investment managers.</p>
            <p className="mb-3">Plan assets are held in trust by an independent custodian in compliance with pension legislation.</p>
            <p className="mb-4">See the Fund's most recent Annual Report, available on the MPF Canada website under the Resources tab, for more information on Fund investments.</p>
            <a
              href="/resources"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-4 py-2.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
            >
              View Annual Reports
              <i className="ri-arrow-right-line text-sm"></i>
            </a>
          </div>
        ),
      },
    ],
  },
];

/* ──────────────────────── sub-components ──────────────────────── */

function AccordionItem({ faq, isOpen, onToggle, id }: { faq: FAQItem; isOpen: boolean; onToggle: () => void; id: string }) {
  return (
    <div id={id} className={`border rounded-md overflow-hidden bg-white transition-colors duration-200 ${isOpen ? "border-[#1a8fc1]/30" : "border-gray-200 hover:border-gray-300"}`}>
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left cursor-pointer hover:bg-[#f4f8fb] transition-colors duration-200 group ${isOpen ? "bg-[#f4f8fc]" : ""}`}
      >
        <span className={`text-sm pr-2 transition-colors duration-200 ${isOpen ? "font-semibold text-[#0d1f4c]" : "font-medium text-[#0d1f4c] group-hover:text-[#0d1f4c]/80"}`}>
          {faq.question}
        </span>
        <span className={`flex-shrink-0 w-5 h-5 flex items-center justify-center transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}>
          <i className="ri-arrow-down-s-line text-lg text-[#1a8fc1]"></i>
        </span>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-out ${
          isOpen ? "max-h-[4000px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-4 pb-4 pt-1 text-sm text-gray-600 leading-relaxed border-t border-gray-200">
          {faq.answer}
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────── page ──────────────────────── */

export default function FAQ() {
  const [searchParams, setSearchParams] = useSearchParams();

  const getCategoryFromParams = (): string => {
    const cat = searchParams.get("category");
    if (cat && faqCategories.some((c) => c.id === cat)) return cat;
    // Backward compat: check old hash
    if (typeof window !== "undefined" && window.location.hash) {
      const hash = window.location.hash.replace("#", "");
      if (hash && faqCategories.some((c) => c.id === hash)) return hash;
    }
    return "the-fund-and-how-it-works";
  };

  const getQuestionFromParams = (): number | null => {
    const q = searchParams.get("question");
    if (q !== null) {
      const idx = parseInt(q, 10);
      const cat = getCategoryFromParams();
      const category = faqCategories.find((c) => c.id === cat);
      if (category && idx >= 0 && idx < category.faqs.length && !isNaN(idx)) {
        return idx;
      }
    }
    return null;
  };

  const [activeId, setActiveId] = useState(getCategoryFromParams);
  const [openIndex, setOpenIndex] = useState<number | null>(getQuestionFromParams);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const newId = getCategoryFromParams();
    setActiveId(newId);
    setOpenIndex(getQuestionFromParams());
  }, [searchParams]);

  const handleCategorySelect = useCallback((id: string) => {
    setActiveId(id);
    setOpenIndex(null);
    setSearchParams({ category: id }, { replace: true });
  }, [setSearchParams]);

  const handleToggle = useCallback((idx: number) => {
    const isOpening = openIndex !== idx;
    const newOpenIndex = openIndex === idx ? null : idx;
    setOpenIndex(newOpenIndex);

    // Update query params
    if (newOpenIndex !== null) {
      setSearchParams({ category: activeId, question: String(newOpenIndex) }, { replace: true });
    } else {
      setSearchParams({ category: activeId }, { replace: true });
    }

    if (isOpening) {
      setTimeout(() => {
        const el = document.getElementById(`faq-${idx}`);
        if (!el) return;

        const headerOffset = 80;
        const bottomPad = 24;

        const answerEl = el.querySelector(".overflow-hidden") as HTMLElement | null;
        if (!answerEl) return;

        const answerBottom = answerEl.getBoundingClientRect().bottom;

        if (answerBottom <= window.innerHeight - bottomPad) {
          return;
        }

        const questionTop = el.getBoundingClientRect().top + window.scrollY - headerOffset;
        window.scrollTo({ top: questionTop, behavior: "smooth" });
      }, 350);
    }
  }, [openIndex, activeId, setSearchParams]);

  const activeCategory = faqCategories.find((c) => c.id === activeId) ?? faqCategories[0];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <Navbar />

      <main className="flex-1">
        {/* ── hero / page header ── */}
        <section className="w-full bg-[#0d1f4c] py-10">
          <div className="max-w-[1200px] mx-auto px-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-0.5 bg-[#1a8fc1]"></div>
              <span className="text-[#7ec8e3] text-xs font-semibold tracking-widest uppercase">
                Help Centre
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
              Frequently Asked Questions
            </h1>
            <p className="text-base text-white/70 max-w-[560px]">
              Find answers to common questions about the Plan, benefits, contributions, life events, and member account access.
            </p>
          </div>
        </section>

        {/* ── mobile category dropdown ── */}
        <div className="lg:hidden w-full bg-[#f4f8fc] border-b border-gray-100 py-4">
          <div className="max-w-[1200px] mx-auto px-6">
            <label htmlFor="faq-cat-select" className="sr-only">Select FAQ Category</label>
            <select
              id="faq-cat-select"
              value={activeId}
              onChange={(e) => handleCategorySelect(e.target.value)}
              className="w-full text-sm font-medium text-[#0d1f4c] bg-white border border-gray-200 rounded-lg px-4 py-3 appearance-none cursor-pointer focus:outline-none focus:border-[#1a8fc1] focus:ring-2 focus:ring-[#1a8fc1]/20"
              style={{
                backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%230d1f4c' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
                paddingRight: "40px",
              }}
            >
              {faqCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.title}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ── two-column body ── */}
        <div className="w-full bg-white">
          <div className="max-w-[1200px] mx-auto px-6 flex flex-col lg:flex-row gap-0">
            {/* left sidebar — desktop only */}
            <aside className="hidden lg:block w-[280px] flex-shrink-0 border-r border-gray-100 py-8 pr-6">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">
                Categories
              </h3>
              <nav className="space-y-0.5">
                {faqCategories.map((cat) => {
                  const isActive = cat.id === activeId;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => handleCategorySelect(cat.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
                        isActive
                          ? "bg-[#e8f2f9] text-[#0d1f4c] font-semibold border-l-2 border-[#1a8fc1]"
                          : "text-gray-600 hover:bg-[#f4f8fb] hover:text-[#0d1f4c] font-normal border-l-2 border-transparent"
                      }`}
                    >
                      <span className={`w-6 h-6 flex items-center justify-center flex-shrink-0 ${isActive ? "text-[#1a8fc1]" : "text-gray-400"}`}>
                        <i className={`${cat.icon} text-sm`}></i>
                      </span>
                      <span className="whitespace-nowrap">{cat.title}</span>
                      {isActive && (
                        <span className="ml-auto w-1 h-4 rounded-full bg-[#1a8fc1] flex-shrink-0"></span>
                      )}
                    </button>
                  );
                })}
              </nav>
            </aside>

            {/* right content area */}
            <div className="flex-1 min-w-0 py-8 lg:pl-8">
              {/* section header */}
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-5 h-0.5 bg-[#1a8fc1]"></div>
                  <h2 className="text-lg font-bold text-[#0d1f4c]">
                    {activeCategory.title}
                  </h2>
                </div>
              </div>

              {/* accordion list */}
              <div className="bg-[#fafbfd] rounded-lg border border-gray-100 p-5">
                <div className="space-y-2.5">
                {activeCategory.faqs.map((faq, idx) => (
                  <AccordionItem
                    key={idx}
                    faq={faq}
                    isOpen={openIndex === idx}
                    onToggle={() => handleToggle(idx)}
                    id={`faq-${idx}`}
                  />
                ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}